import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { ModalComponent } from '../../../../shared/components/ui/modal/modal.component';
import { MarketingFormsService } from '../../service/marketing-forms.service';
import { extractApiErrorMessage } from '../../utils/api-error.util';
import { AuditApiService } from '../../../audit/services/audit-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { AuthApiService } from '../../../auth-pages/services/auth-api.service';
import { DatetimeService } from '../../../../shared/services/datetime.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { AuditLogResponse } from '../../../audit/models/audit.model';
import { FormListModel, FormType } from '../../models/form.model';
import { MktFormNote } from '../../models/form-note.model';
import { FormTypeLabelPipe } from '../../pipes/form-type-label.pipe';
import { FormStatusPipe } from '../../pipes/form-status.pipe';
import { FormAuditTimelineComponent } from '../form-audit-timeline/form-audit-timeline.component';
import { buildPublicFormUrl } from '../../utils/public-form-url';

@Component({
  selector: 'app-form-detail-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent, FormTypeLabelPipe, FormStatusPipe, FormAuditTimelineComponent],
  templateUrl: './form-detail-modal.component.html',
})
export class FormDetailModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() form: FormListModel | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() edit = new EventEmitter<number>();
  @Output() updated = new EventEmitter<void>();

  @ViewChild('nameInput') nameInputRef?: ElementRef<HTMLInputElement>;

  activeTab: 'info' | 'timeline' = 'info';

  createdByUser: any = null;
  loadingUser = false;
  formDescription: string | undefined = undefined;
  formInternalNotes: string | undefined = undefined;

  editingName = false;
  editNameValue = '';
  savingName = false;

  editingNotes = false;
  editNotesValue = '';
  savingNotes = false;

  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  // inc29: notas internas múltiples
  notes: MktFormNote[] = [];
  loadingNotes = false;
  newNote = '';
  savingNote = false;

  // Emite en reset() para cancelar requests en vuelo al cerrar/cambiar de formulario
  private readonly load$ = new Subject<void>();

  constructor(
    private formsService: MarketingFormsService,
    private auditApi: AuditApiService,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private datetimeService: DatetimeService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService,
    private router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['form']) {
      if (this.isOpen && this.form?.id) {
        this.activeTab = 'info';
        this.createdByUser = null;
        this.loadCreatedBy();
        this.loadNotes();
      } else if (!this.isOpen) {
        this.reset();
      }
    }
  }

  ngOnDestroy(): void {
    this.load$.next();
    this.load$.complete();
  }

  private reset(): void {
    this.load$.next();
    this.activeTab = 'info';
    this.createdByUser = null;
    this.formDescription = undefined;
    this.formInternalNotes = undefined;
    this.editingName = false;
    this.editNameValue = '';
    this.savingName = false;
    this.editingNotes = false;
    this.editNotesValue = '';
    this.savingNotes = false;
    this.timelineLogs = [];
    this.timelineError = '';
    this.timelinePage = 0;
    this.notes = [];
    this.loadingNotes = false;
    this.newNote = '';
    this.savingNote = false;
  }

  startEditName(): void {
    this.editNameValue = this.form?.name ?? '';
    this.editingName = true;
    setTimeout(() => this.nameInputRef?.nativeElement.focus(), 0);
  }

  saveName(): void {
    if (!this.editingName) return;
    const trimmed = this.editNameValue.trim();
    if (!this.form?.id || this.savingName || !trimmed || trimmed === this.form.name) {
      this.editingName = false;
      return;
    }
    this.savingName = true;
    this.formsService.updateForm(this.form.id, { name: trimmed })
      .pipe(takeUntil(this.load$))
      .subscribe({
        next: () => {
          this.form = { ...this.form!, name: trimmed };
          this.editingName = false;
          this.savingName = false;
          this.updated.emit();
        },
        error: err => {
          this.savingName = false;
          this.editingName = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo guardar el nombre'));
        },
      });
  }

  onNameKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveName();
    } else if (event.key === 'Escape') {
      this.editingName = false;
    }
  }

  startEditNotes(): void {
    this.editNotesValue = this.formInternalNotes ?? '';
    this.editingNotes = true;
  }

  cancelEditNotes(): void {
    this.editingNotes = false;
    this.editNotesValue = '';
  }

  saveNotes(): void {
    if (!this.form?.id || this.savingNotes) return;
    this.savingNotes = true;
    this.formsService.updateForm(this.form.id, { internalNotes: this.editNotesValue })
      .pipe(takeUntil(this.load$))
      .subscribe({
        next: () => {
          this.formInternalNotes = this.editNotesValue || undefined;
          this.editingNotes = false;
          this.savingNotes = false;
          this.updated.emit();
        },
        error: err => {
          this.savingNotes = false;
          this.toast.error(extractApiErrorMessage(err, 'No se pudo guardar las notas internas'));
        },
      });
  }

  private loadCreatedBy(): void {
    this.formsService.getFormById(this.form!.id).pipe(
      catchError(() => of(null)),
      takeUntil(this.load$),
    ).subscribe(detail => {
      this.formDescription = detail?.description;
      this.formInternalNotes = detail?.internalNotes;
      const creatorId = detail?.createdBy;
      if (!creatorId) return;
      const currentUser = this.authService.getCurrentUser();
      if (currentUser?.id === creatorId) {
        this.createdByUser = currentUser;
      } else {
        this.loadingUser = true;
        this.authApiService.getUserById(creatorId).pipe(
          catchError(() => of(null)),
          takeUntil(this.load$),
        ).subscribe(u => {
          this.createdByUser = u;
          this.loadingUser = false;
        });
      }
    });
  }

  onTabChange(tab: 'info' | 'timeline'): void {
    this.activeTab = tab;
    if (tab === 'timeline' && this.timelineLogs.length === 0 && !this.loadingTimeline) {
      this.loadTimeline(0);
    }
  }

  // ── Notas internas (inc29) ────────────────────────────────────────────────

  private loadNotes(): void {
    if (!this.form?.id) return;
    this.loadingNotes = true;
    this.formsService.getNotes(this.form.id).pipe(
      catchError(() => of([] as MktFormNote[])),
      takeUntil(this.load$),
    ).subscribe(notes => {
      this.notes = notes ?? [];
      this.loadingNotes = false;
    });
  }

  addNote(): void {
    const note = this.newNote.trim();
    if (!this.form?.id || !note || this.savingNote) return;
    this.savingNote = true;
    this.formsService.createNote(this.form.id, { note }).pipe(takeUntil(this.load$)).subscribe({
      next: created => {
        this.notes = [created, ...this.notes];
        this.newNote = '';
        this.savingNote = false;
        this.toast.created('Nota');
      },
      error: err => {
        this.savingNote = false;
        this.toast.error(extractApiErrorMessage(err, 'No se pudo agregar la nota'));
      },
    });
  }

  async removeNote(note: MktFormNote): Promise<void> {
    if (!this.form?.id) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar nota',
      message: '¿Eliminar esta nota interna? Esta acción no se puede deshacer.',
    });
    if (!ok) return;
    this.formsService.deleteNote(this.form.id, note.id).pipe(takeUntil(this.load$)).subscribe({
      next: () => {
        this.notes = this.notes.filter(n => n.id !== note.id);
        this.toast.deleted('Nota');
      },
      error: err => this.toast.error(extractApiErrorMessage(err, 'No se pudo eliminar la nota')),
    });
  }

  loadTimeline(page = 0): void {
    if (!this.form?.id) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;
    // inc28: una sola línea de tiempo consolidada (formulario + sub-eventos con subtipo).
    this.auditApi.getEntityTimeline('mkt_form', String(this.form.id), { page, size: 20 }).pipe(takeUntil(this.load$)).subscribe({
      next: data => {
        this.timelineLogs = data.content ?? [];
        this.timelinePage = data.page ?? page;
        this.timelineTotalPages = data.totalPages ?? 1;
        this.timelineTotalElements = data.totalElements ?? 0;
        this.loadingTimeline = false;
      },
      error: err => {
        this.timelineError = extractApiErrorMessage(err, 'Error al cargar la línea de tiempo.');
        this.loadingTimeline = false;
      },
    });
  }

  get initials(): string {
    const name = this.form?.name ?? '';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  get avatarColorClass(): string {
    const colors = [
      'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
      'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
    ];
    const name = this.form?.name ?? '';
    const index = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return colors[index % colors.length];
  }

  get statusColor(): string {
    switch (this.form?.status) {
      case 'PUBLISHED': return 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400';
      case 'DRAFT':     return 'bg-warning-50 text-warning-700 dark:bg-warning-500/10 dark:text-warning-400';
      case 'PAUSED':    return 'bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400';
      case 'ARCHIVED':  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
      default:          return 'bg-gray-100 text-gray-600';
    }
  }

  get typeColorClass(): string {
    const map: Record<FormType, string> = {
      INLINE:     'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-400',
      POPUP:      'bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400',
      STICKY_BAR: 'bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400',
      SLIDE_IN:   'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
    };
    return this.form ? (map[this.form.formType] ?? 'bg-gray-100 text-gray-600') : 'bg-gray-100 text-gray-600';
  }

  formatDate(d?: string): string {
    return this.datetimeService.formatDateTime(d);
  }

  // inc35: misma URL pública completa que el editor (pestaña Distribución).
  getPublicUrl(): string {
    return buildPublicFormUrl(this.form?.slug);
  }

  copyPublicUrl(): void {
    const url = this.getPublicUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => this.toast.success('Enlace copiado al portapapeles'),
      () => this.toast.error('No se pudo copiar el enlace'),
    );
  }

  onClose(): void {
    this.close.emit();
  }

  onEdit(): void {
    this.edit.emit(this.form!.id);
    this.close.emit();
  }
}
